import { useMemo } from "react";
import type { RuleOverrides } from "../../data/ruleRegistry";
import {
	type BuildProgress,
	getProjectBuildGuide,
} from "../../domain/build/getProjectBuildGuide";
import { calculateFinishPriceDeltas } from "../../domain/finishes/finishPriceDeltas";
import { DEFAULT_FINISHES } from "../../domain/finishes/types";
import { resolveWallHeightM } from "../../domain/geometry/buildingDimensions";
import { calculateHeaterRequirements } from "../../domain/heater/calculateHeaterRequirements";
import { installedHeaterKw } from "../../domain/heater/heaterCatalog";
import { normalizeIndoorRoom } from "../../domain/indoor/indoorRoom";
import {
	type ClimateProfile,
	getClimateProfileOption,
	type LocationMode,
	type Market,
} from "../../domain/location/types";
import { getRoofConfigSummaryLabel } from "../../domain/roof/roofConfig";
import { rotateSlopeToModelFrame } from "../../domain/viewer/saunaRotation";
import {
	heaterGlassInput,
	resolveHeaterGlassArea,
} from "../../engines/heaterGlassArea";
import {
	resolvePartitionSplit,
	resolveTwoRoomConfig,
} from "../../engines/twoRoomSauna";
import type {
	BuildInput,
	DeckConfig,
	RoofConfig,
	SaunaEngine,
	TwoRoomConfig,
} from "../../engines/types";
import { type Dictionary, getT } from "../../i18n";
import type { SaunaType } from "../../types/design";
import { buildRunningCostSummary } from "./runningCostSummary";
import {
	buildCompletenessSignal,
	buildWorkspaceBadges,
} from "./workspaceBadges";
import { type BuilderWorkspaceMeta, getNextWorkspaceMeta } from "./workspaces";

/** Map internal SaunaType to the correct i18n key */
function getLocalizedTypeLabel(saunaType: SaunaType, t: Dictionary): string {
	switch (saunaType) {
		case "fyrkantig":
			return t.dashboard_typeSquare;
		case "tunna":
			return t.dashboard_typeBarrel;
		case "tvarum":
			return t.dashboard_typeTwoRoom;
		case "inomhus":
			return t.dashboard_typeIndoor;
		default:
			return saunaType;
	}
}

function countOpeningTypes(openings: BuildInput["openings"]) {
	return openings.reduce(
		(accumulator, opening) => {
			if (opening.type === "door") {
				accumulator.doors += 1;
			}
			if (opening.type === "window") {
				accumulator.windows += 1;
			}
			return accumulator;
		},
		{ doors: 0, windows: 0 },
	);
}

function getBuilderHeaterVolumes(
	saunaType: SaunaType,
	dimensions: BuildInput["dimensions"],
	partitionSplit: number,
	twoRoomConfig: Partial<TwoRoomConfig> | undefined,
	wallHeightM: number | undefined,
) {
	// The same height the engine's volume uses, so the fallback kW cannot
	// size a different room than the BOM's (issue 155).
	const ceilingHeightM = resolveWallHeightM(wallHeightM);
	const totalVolumeM3 = dimensions.width * dimensions.length * ceilingHeightM;
	if (saunaType !== "tvarum") {
		return { volM3: totalVolumeM3, totalVolumeM3 };
	}

	const config = resolveTwoRoomConfig(twoRoomConfig);
	return {
		volM3: config.hasRelaxRoom
			? dimensions.width *
				dimensions.length *
				resolvePartitionSplit(partitionSplit, dimensions.length, true) *
				ceilingHeightM
			: totalVolumeM3,
		totalVolumeM3,
	};
}

interface BuilderDerivedDataOptions {
	engine: SaunaEngine;
	saunaType: SaunaType;
	dimensions: BuildInput["dimensions"];
	partitionSplit: number;
	openings: BuildInput["openings"];
	deckConfig: DeckConfig | null;
	roofConfig: RoofConfig;
	thermalInputs: BuildInput["thermalInputs"];
	siteConditions: BuildInput["siteConditions"];
	/** `null` (the default) hands the interior to the engine's generator. */
	interiorLayout?: BuildInput["interiorLayout"];
	/** Character choices (issue 134); absent means the defaults. */
	finishes?: BuildInput["finishes"];
	/** Chosen wall height (issue 155); `null`/absent = the rule default. */
	wallHeightM?: number | null;
	/** Inomhus only (issues 174/175); `null`/absent = free-standing. */
	indoorRoom?: BuildInput["indoorRoom"] | null;
	/**
	 * Whole-building orientation. Only reaches the engine through the site
	 * conditions — see below.
	 */
	rotationDeg?: number;
	market: Market;
	locationMode: LocationMode;
	climateProfile: ClimateProfile;
	postalCode: string;
	overrides: RuleOverrides;
	activeWorkspaceMeta: BuilderWorkspaceMeta;
	twoRoomConfig?: Partial<TwoRoomConfig>;
	buildProgress: BuildProgress;
}

export function useBuilderDerivedData({
	engine,
	saunaType,
	dimensions,
	partitionSplit,
	openings,
	deckConfig,
	roofConfig,
	thermalInputs,
	siteConditions,
	interiorLayout = null,
	finishes = DEFAULT_FINISHES,
	wallHeightM = null,
	indoorRoom = null,
	rotationDeg = 0,
	market,
	locationMode,
	climateProfile,
	postalCode,
	overrides,
	activeWorkspaceMeta,
	twoRoomConfig,
	buildProgress,
}: BuilderDerivedDataOptions) {
	// The terrain is drawn in world space and never turns, but the engine reads
	// the slope at MODEL pier positions — so the slope is what has to move
	// frames. On a flat site (the default) this returns the same object, so
	// `buildInput` is unchanged and rotation stays a pure transform with no
	// rebuild. On a slope it genuinely changes the build, and should: which
	// plinths stand tall, and how deep the entry steps drop, depends on which
	// way the building faces. That is also the ONLY path by which rotation
	// reaches the engine — it is deliberately not a `BuildInput` field.
	const siteConditionsInModelFrame = useMemo(
		() =>
			siteConditions
				? rotateSlopeToModelFrame(siteConditions, rotationDeg)
				: siteConditions,
		[siteConditions, rotationDeg],
	);

	const buildInput = useMemo<BuildInput>(
		() => ({
			dimensions,
			openings,
			partitionSplit: saunaType === "tvarum" ? partitionSplit : undefined,
			deckConfig,
			roofConfig,
			thermalInputs,
			siteConditions: siteConditionsInModelFrame,
			interiorLayout,
			finishes,
			// A barrel has no walls; an unchosen height stays off the input so
			// the rule-driven defaults apply exactly as before issue 155.
			...(saunaType !== "tunna" && wallHeightM !== null ? { wallHeightM } : {}),
			market,
			locationMode,
			climateProfile:
				locationMode === "manual_climate" ? climateProfile : undefined,
			overrides,
			postalCode: postalCode || undefined,
			...(saunaType === "tvarum" ? twoRoomConfig : {}),
			// The engine wrapper sets `siting` itself; it is repeated here so
			// the viewer's scene (which reads `buildInput`, not the engine) can
			// swap the forest for the room (issue 175).
			...(saunaType === "inomhus"
				? {
						siting: "indoor" as const,
						indoorRoom: normalizeIndoorRoom(indoorRoom),
					}
				: {}),
		}),
		[
			climateProfile,
			deckConfig,
			dimensions,
			finishes,
			indoorRoom,
			interiorLayout,
			locationMode,
			market,
			openings,
			overrides,
			partitionSplit,
			postalCode,
			roofConfig,
			saunaType,
			siteConditionsInModelFrame,
			thermalInputs,
			twoRoomConfig,
			wallHeightM,
		],
	);

	const calcData = useMemo(
		() => engine.calculateMaterials(buildInput),
		[buildInput, engine],
	);
	const heaterVolumes = useMemo(
		() =>
			getBuilderHeaterVolumes(
				saunaType,
				dimensions,
				partitionSplit,
				twoRoomConfig,
				buildInput.wallHeightM,
			),
		[
			buildInput.wallHeightM,
			dimensions,
			partitionSplit,
			saunaType,
			twoRoomConfig,
		],
	);

	// The engine's own sizing — it reads the glass off the built openings
	// (issue 165). The fallback only runs for a degraded calcData (see the
	// partial-calcData note) and derives glass through the same helper.
	const heater = useMemo(
		() =>
			calcData.heaterRequirements ??
			calculateHeaterRequirements(
				{
					volM3: heaterVolumes.volM3,
					totalVolumeM3: heaterVolumes.totalVolumeM3,
					...heaterGlassInput(
						resolveHeaterGlassArea(
							buildInput,
							saunaType === "tvarum"
								? resolveTwoRoomConfig(twoRoomConfig)
								: undefined,
						),
					),
					stoneAreaSqm: thermalInputs.stoneAreaSqm,
					isOutdoorLogCabin: thermalInputs.isOutdoorLogCabin,
					heaterType: thermalInputs.heaterType,
					hasRelaxRoom: twoRoomConfig?.hasRelaxRoom,
				},
				overrides,
				market,
			),
		[
			calcData.heaterRequirements,
			heaterVolumes,
			buildInput,
			saunaType,
			twoRoomConfig,
			thermalInputs,
			overrides,
			market,
		],
	);

	const estimatedTotal = useMemo(() => {
		return calcData.pricingSummary?.materialCostSek ?? 0;
	}, [calcData.pricingSummary]);

	// Live heat-up time and cost per session. Derived from the same
	// split-aware volume and heater requirement the rest of the builder uses,
	// so the numbers react to every dimension, glass and heater-type change
	// without a second calculateMaterials run.
	const runningCost = useMemo(
		() =>
			buildRunningCostSummary({
				calcData,
				volumeM3: heaterVolumes.volM3,
				// The chosen model's rating when there is one (issue 159).
				heaterKw: installedHeaterKw(thermalInputs, heater.recommendedKw),
				thermalInputs,
				market,
				t: getT(market),
			}),
		[
			calcData,
			heaterVolumes.volM3,
			heater.recommendedKw,
			thermalInputs,
			market,
		],
	);

	// Live workspace-nav badges + rail completeness signal. Memoized off the
	// already-computed calcData (no extra calculateMaterials run, nothing added
	// to the store) so tab switches stay cheap and the badges track every
	// dimension/rule change automatically.
	// The one build guide for this draft. The Build tab renders it and the
	// workspace badge counts its steps, so the tab and the guide can never
	// disagree about how many steps there are — and it is built for the
	// active market, so intl never sees the Swedish permit gates.
	const buildGuide = useMemo(
		() =>
			getProjectBuildGuide({
				data: calcData,
				width: dimensions.width,
				length: dimensions.length,
				openings,
				thermalInputs,
				market,
				engineType: saunaType,
			}),
		[
			calcData,
			dimensions.width,
			dimensions.length,
			openings,
			thermalInputs,
			market,
			saunaType,
		],
	);

	const workspaceBadges = useMemo(
		() =>
			buildWorkspaceBadges({
				calcData,
				buildGuide,
				buildProgress,
				t: getT(market),
				market,
			}),
		[calcData, buildGuide, buildProgress, market],
	);

	const completeness = useMemo(
		() =>
			buildCompletenessSignal({
				hasLocation:
					locationMode !== "postal_code" || postalCode.trim().length > 0,
				hasOpenings: openings.length > 0,
				t: getT(market),
			}),
		[locationMode, postalCode, openings, market],
	);

	const t = getT(market);
	const openingCounts = countOpeningTypes(openings);
	const floorArea = dimensions.width * dimensions.length;
	const climateOption = getClimateProfileOption(climateProfile.snowLoadClass);
	const summaryItems = [
		{ label: t.summary_type, value: getLocalizedTypeLabel(saunaType, t) },
		{
			label: t.summary_size,
			value: `${dimensions.width.toFixed(1)} × ${dimensions.length.toFixed(1)} m`,
		},
		{
			label: t.summary_roof,
			value: getRoofConfigSummaryLabel(roofConfig, market),
		},
		{ label: t.summary_area, value: `${floorArea.toFixed(1)} m²` },
		{
			label: t.summary_openings,
			value: `${openingCounts.doors} ${openingCounts.doors === 1 ? t.dim_openingDoorSingular : t.dim_openingDoorPlural} • ${openingCounts.windows} ${openingCounts.windows === 1 ? t.dim_openingWindow : t.dim_openingWindowPlural}`,
		},
		{
			label: t.summary_heating,
			value:
				thermalInputs.heaterType === "wood"
					? t.summary_heaterWood
					: t.summary_heaterElectric,
		},
		{
			label: t.location_summaryLabel,
			value:
				locationMode === "manual_climate"
					? `${climateOption.label} • snow zone ${climateProfile.snowZone.toFixed(1)}`
					: postalCode
						? `${t.summary_postalPrefix} ${postalCode}`
						: t.location_summaryFallback,
		},
	];

	// Priced off the SAME rows the pricing summary sums, and re-derived only
	// when those rows or the selection move — a swatch label must never quote a
	// delta the total then disagrees with.
	const finishPriceDeltas = useMemo(
		() =>
			calculateFinishPriceDeltas({
				materials: calcData.materials,
				finishes,
				basis: calcData.finishBasis,
			}),
		[calcData.materials, calcData.finishBasis, finishes],
	);

	return {
		buildInput,
		calcData,
		finishPriceDeltas,
		heater,
		estimatedTotal,
		runningCost,
		floorArea,
		summaryItems,
		buildGuide,
		workspaceBadges,
		completeness,
		nextWorkspaceMeta: getNextWorkspaceMeta(activeWorkspaceMeta, market),
	};
}
